import { z } from 'zod';

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const REPORT_NAMES = ['trips', 'stops', 'speeding', 'daily-distance', 'expenses', 'fuel', 'maintenance', 'invoices', 'device-offline', 'alerts'] as const;
export type ReportName = (typeof REPORT_NAMES)[number];

export const EXPORT_FORMATS = ['xlsx', 'csv'] as const;
export type ExportFormat = (typeof EXPORT_FORMATS)[number];

export const ReportQuerySchema = z.object({
  from: isoDate,
  to: isoDate,
  vehicleId: z.string().uuid().optional(),
  driverId: z.string().uuid().optional(),
  groupId: z.string().uuid().optional(),
  customerId: z.string().uuid().optional(),
  /** max rows returned inline (exports are unbounded) */
  limit: z.coerce.number().int().min(1).max(5000).default(1000),
});
export type ReportQuery = z.infer<typeof ReportQuerySchema>;

export const ExportRequestSchema = ReportQuerySchema.omit({ limit: true }).extend({
  format: z.enum(EXPORT_FORMATS).default('xlsx'),
  lang: z.enum(['ar', 'en']).default('ar'),
});
export type ExportRequest = z.infer<typeof ExportRequestSchema>;

export type ReportColumnType = 'text' | 'number' | 'money' | 'date' | 'datetime' | 'duration' | 'km' | 'kph' | 'percent';

export interface ReportColumn {
  key: string;
  labelAr: string;
  labelEn: string;
  type: ReportColumnType;
  /** show the column total in the footer */
  sum?: boolean;
}

export interface ReportResult {
  name: ReportName;
  columns: ReportColumn[];
  rows: Record<string, string | number | null>[];
  totals: Record<string, number>;
  meta: { from: string; to: string; rowCount: number; truncated: boolean; generatedAt: string };
}

export interface ExportJobStatus {
  jobId: string;
  state: 'waiting' | 'active' | 'completed' | 'failed' | 'unknown';
  fileId: string | null;
  url: string | null;
  fileName: string | null;
  error: string | null;
}

/** Job payload on the `exports` queue. */
export interface ExportJob {
  tenantId: string;
  userId: string;
  name: ReportName;
  query: ExportRequest;
}
